import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';

/**
 * Компонент для защиты маршрутов, требующих аутентификации.
 * 
 * Если пользователь аутентифицирован, он отображает дочерние компоненты.
 * Если пользователь не аутентифицирован, он автоматически перенаправляет
 * его на страницу входа, сохраняя исходный URL для последующего возврата.
 * 
 * @param {object} props - Свойства компонента.
 * @param {React.ReactNode} props.children - Защищаемый компонент/страница.
 */
const PrivateRoute = ({ children }) => {
    const { isAuthenticated, isInitializing } = useAuth();
    const location = useLocation();

    // Пока контекст аутентификации инициализируется (например, проверяет токен в localStorage),
    // показываем полноэкранный загрузчик, чтобы избежать "моргания" интерфейса.
    if (isInitializing) {
        return <LoadingSpinner message="Проверка аутентификации..." fullScreen />;
    }

    // Если инициализация завершена, и мы точно знаем, что пользователь НЕ вошел в систему,
    // выполняем перенаправление на страницу входа.
    if (!isAuthenticated) {
        // Компонент Navigate из react-router-dom выполняет редирект.
        // `replace` предотвращает добавление страницы-редиректа в историю браузера.
        // `state={{ from: location }}` передает информацию о том, откуда пришел пользователь,
        // чтобы после логина его можно было вернуть на исходную страницу.
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Если все проверки пройдены и пользователь аутентифицирован,
    // просто отображаем запрошенную страницу.
    return children;
};

/**
 * Компонент для страниц, доступных только неаутентифицированным пользователям
 * (например, страницы входа и регистрации).
 * 
 * Если аутентифицированный пользователь попытается зайти на такую страницу,
 * он будет автоматически перенаправлен на указанный маршрут (по умолчанию - в личный кабинет).
 * 
 * @param {object} props - Свойства компонента.
 * @param {React.ReactNode} props.children - Страница для гостей.
 * @param {string} [props.redirectTo='/dashboard'] - Путь для редиректа аутентифицированных пользователей.
 */
export const GuestRoute = ({ children, redirectTo = '/dashboard' }) => {
    const { isAuthenticated, isInitializing } = useAuth();

    // Показываем загрузчик во время инициализации.
    if (isInitializing) {
        return <LoadingSpinner message="Загрузка..." fullScreen />;
    }

    // Если пользователь уже вошел в систему, перенаправляем его.
    if (isAuthenticated) {
        return <Navigate to={redirectTo} replace />;
    }

    // Если пользователь - гость, отображаем страницу.
    return children;
};

export default PrivateRoute;